import dayjs from 'dayjs';
import { lazy, useState, Suspense } from 'react';
import { Rating } from 'react-simple-star-rating';
import { useParams, useNavigate } from 'react-router-dom';

import { useHeader } from 'context';
import { List } from 'components/list';
import { Link } from 'components/link';
import { formatDate } from 'utils/date';
import { Badge } from 'components/badge';
import { InfoBlock } from 'components/info-block';
import { BadgeColor, BackOfficeRoles } from 'types';
import { ListHeader } from 'components/list-header';
import { states, Messages, AppRoutes } from 'config';
import { GeneralInfo } from 'components/general-info';
import { useAppDispatch, useAppSelector } from 'store';
import { DeleteDialog } from 'components/delete-dialog';
import { EventPayments } from 'components/event-payments';
import { formatPhone, addPercentageLabel } from 'utils/helpers';
import { eventApi, tagTypes, useRemoveMutation } from 'store/api/event';
import { useProjectsQuery, useConnectionQuery } from 'store/api/quickbooks';
import {
  selectAuthUser,
  isEventManagerRole,
  isCompanyOwnerOrSiteAdmin,
} from 'store/slices/auth';

import { EventDetailsProps } from './types';

// TRIPS: the Location page's Logistics + Travel cost cards load in their own
// chunk (the main bundle does not carry the Trips UI).
const LogisticsCards = lazy(() =>
  import('./logistics-card').then((module) => ({
    default: module.LogisticsCards,
  }))
);
import { formatOptionalPrice } from './format';
import { LocationListCard } from './location-card';
import { EventCardMachines } from './card-machines';
import {
  getLocationHeader,
  getShowHoursSection,
  getQuickBooksLinkStatus,
} from './helpers';

// Notes are free text: wrap them in full and keep the typed line breaks
// (pre-line). A long note keeps a readable line length (~80 characters).
const freeText = (text?: string | null) =>
  text?.trim() ? (
    <span
      style={{
        maxWidth: '70ch',
        display: 'block',
        whiteSpace: 'pre-line',
        overflowWrap: 'anywhere',
      }}
    >
      {text.trim()}
    </span>
  ) : undefined;

const displayEventReportRoles = [
  BackOfficeRoles.ACCOUNTING,
  BackOfficeRoles.ADMINISTRATIVE,
];

export const EventDetails = ({
  data,
  isModal,
  editPath,
  titleRightSlot,
}: EventDetailsProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authUser = useAppSelector(selectAuthUser);
  const isManager = useAppSelector(isEventManagerRole);
  const isAdminRoles = useAppSelector(isCompanyOwnerOrSiteAdmin);
  const [isOpenDeleteDialog, setOpenDeleteDialog] = useState(false);

  const authUserRole = authUser?.role?.name;
  const isUserTypeOps = authUser?.userType === 'ops';
  const isSalesRep = authUserRole === BackOfficeRoles.SALES_REP;
  const isQuickBooksHiddenRole = [
    BackOfficeRoles.MANAGER,
    BackOfficeRoles.ADMIN_REP,
    BackOfficeRoles.SALES_REP,
  ].includes(authUserRole as BackOfficeRoles);
  const qbConnectionQuery = useConnectionQuery('production', {
    skip: isQuickBooksHiddenRole,
  });
  const qbConnection = qbConnectionQuery.data;
  const qbRealmId =
    qbConnection?.status === 'active' ? qbConnection.realmId : undefined;
  const qbProjectsQuery = useProjectsQuery(
    { realmId: qbRealmId || '', environment: 'production' },
    { skip: isQuickBooksHiddenRole || !qbRealmId }
  );
  const quickBooksLink = getQuickBooksLinkStatus({
    eventName: data?.name,
    eventId: data?.id ?? '',
    connection: qbConnection,
    links: qbProjectsQuery.data?.links,
    isProjectsError: qbProjectsQuery.isError,
    isConnectionError: qbConnectionQuery.isError,
    isProjectsLoading: qbProjectsQuery.isLoading,
    isConnectionLoading: qbConnectionQuery.isLoading,
  });
  const isManualCreditPaymentEnabled = data?.isManualCreditPaymentEnabled;
  const showHours = getShowHoursSection(data ?? {});
  const isDisplayReportRole = displayEventReportRoles?.includes(
    authUserRole as BackOfficeRoles
  );
  const isDisplayReport =
    isDisplayReportRole || isAdminRoles || data?.leaderId === authUser?.id;

  const [remove, removeMutation] = useRemoveMutation();

  useHeader({ pageName: 'Event', showBackButton: true });

  const isHideForSalesAndManager = isSalesRep || isManager;

  const locationTitles = getLocationHeader(isHideForSalesAndManager);

  const isHideForSalesAndManagerOrModal = isHideForSalesAndManager || isModal;

  if (!data) {
    return null;
  }

  const details = (
    <GeneralInfo
      photoSize={128}
      withPlaceholders
      name={data.name}
      withBorder={false}
      photo={data.photoUrl}
      disableSticky={isModal}
      stickyWithHeader={false}
      sectionWrapper={InfoBlock}
      containerStyles={{ gap: '24px' }}
      titleRightSlot={isDisplayReport && !isModal ? titleRightSlot : undefined}
      onEdit={
        isHideForSalesAndManager
          ? undefined
          : () => navigate(editPath || 'edit')
      }
      subHeader={
        isAdminRoles && isManualCreditPaymentEnabled ? (
          <div>
            <Badge styleType={BadgeColor.Blue} text="Manual Credit Enabled" />
          </div>
        ) : undefined
      }
      rightSlot={
        isHideForSalesAndManagerOrModal ? undefined : (
          <DeleteDialog
            id={id!}
            action={remove}
            title="Delete event"
            name={data?.name ?? ''}
            mutation={removeMutation}
            open={isOpenDeleteDialog}
            navigateUrl={AppRoutes.Events.Home}
            onClose={() => setOpenDeleteDialog(false)}
            successMessage={`Event ${Messages.DELETED_SUCCESSFULLY}`}
            invalidate={() => dispatch(eventApi.util.invalidateTags(tagTypes))}
          />
        )
      }
      sections={[
        {
          withBorder: false,
          title: 'General information',
          rows: [
            [
              {
                col: 3,
                title: 'ID',
                colTablet: 6,
                colMobile: 3,
                value: data.id,
              },
              {
                col: 3,
                colTablet: 6,
                colMobile: 9,
                title: 'Between dates',
                value:
                  data.startDate && data.endDate
                    ? `${formatDate(dayjs(data.startDate))} - ${formatDate(dayjs(data.endDate))}`
                    : '',
              },
              {
                col: 3,
                colTablet: 6,
                title: 'Selling Days',
                value: data.sellingDays,
              },
              {
                col: 3,
                colTablet: 6,
                title: 'Address',
                value: data.address,
              },
            ],
            [
              {
                col: 3,
                title: 'Tax',
                colTablet: 6,
                colMobile: 6,
                value: data.tax ? addPercentageLabel(data.tax) : undefined,
              },
              {
                col: 3,
                colTablet: 6,
                colMobile: 6,
                title: 'State',
                value: data.state && states[data.state]?.name,
              },
              {
                col: 3,
                colTablet: 6,
                colMobile: 6,
                title: 'City',
                value: data.city,
              },
              {
                col: 3,
                title: 'ZIP',
                colTablet: 6,
                colMobile: 6,
                value: data.zip,
              },
            ],
            [
              {
                col: 3,
                colTablet: 6,
                title: 'Timezone',
                value: data?.timezoneId ? data.timezone?.code : undefined,
              },
              ...(!isQuickBooksHiddenRole
                ? [
                    {
                      col: 3,
                      colTablet: 6,
                      title: 'QuickBooks Project',
                      value: quickBooksLink.projectName,
                    },
                    {
                      col: 3,
                      colTablet: 6,
                      title: 'QuickBooks Status',
                      value: quickBooksLink.status,
                    },
                  ]
                : []),
            ],
          ],
        },
        ...(showHours.items.length
          ? [
              {
                withBorder: false,
                title: showHours.title,
                style: { paddingTop: 0 },
                rows: [
                  showHours.items.map(({ title, value }) => ({
                    title,
                    value,
                    col: 3,
                    colTablet: 4,
                    colMobile: 6,
                  })),
                ],
              },
            ]
          : []),
        ...(!isHideForSalesAndManager
          ? [
              {
                withBorder: false,
                title: 'Contact info',
                style: { paddingTop: 0 },
                rows: [
                  [
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Contact Name',
                      value: data.contactName,
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Phone',
                      value: formatPhone(data.contactPhone),
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Email',
                      value: data.contactEmail,
                    },
                  ],
                ],
              },
            ]
          : []),
        {
          withBorder: false,
          style: { paddingTop: 0 },
          title: 'Event theme & Products',
          rows: [
            [
              {
                col: 4,
                colTablet: 4,
                title: 'Theme',
                value: data.theme?.name,
              },
              { col: 4, colTablet: 4, title: 'Link', value: data.link },
              {
                col: 4,
                colTablet: 4,
                title: 'Product',
                value: data.product?.name,
              },
            ],
          ],
        },
        ...(!isHideForSalesAndManager
          ? [
              {
                withBorder: false,
                title: 'Attendance',
                style: { paddingTop: 0 },
                rows: [
                  [
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Attendance',
                      value: data.attendance,
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Vendors',
                      value: data.vendors,
                    },
                  ],
                  [
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Event Rating',
                      value: <Rating readonly initialValue={data.rating} />,
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      value: data.ratingMessage,
                      title: 'Event Rating Message',
                    },
                  ],
                ],
              },
            ]
          : []),

        {
          rows: [
            [
              {
                col: 4,
                colTablet: 6,
                title: 'Floor Plan File',
                value: data.floorPlanFileUrl && (
                  <Link download url={data.floorPlanFileUrl}>
                    Download
                  </Link>
                ),
              },
              {
                col: 4,
                colTablet: 6,
                title: 'Floor Plan Link',
                value: data.floorPlanLink,
              },
              {
                col: 4,
                colTablet: 6,
                title: 'Final Price',
                hidden: isHideForSalesAndManager,
                value: formatOptionalPrice(data.finalPrice),
              },
            ],
            [
              isUserTypeOps
                ? {
                    col: 4,
                    colTablet: 6,
                    noEllipsis: true,
                    title: 'Booking Team Notes',
                    value: freeText(data.bookingTeamNotes),
                  }
                : {},
            ],
          ],
        },
        ...(data.locations.length
          ? [
              {
                withBorder: false,
                title: 'Locations',
                style: { paddingTop: 0 },
                children: (
                  <List
                    count={data.locations?.length}
                    header={<ListHeader titles={locationTitles} />}
                  >
                    {data.locations?.map((location) => (
                      <LocationListCard
                        key={location.id}
                        location={location}
                        titles={locationTitles}
                        isHideForSalesAndManager={isHideForSalesAndManager}
                      />
                    ))}
                  </List>
                ),
              },
            ]
          : []),
        ...(data.selectedLocations?.length
          ? [
              {
                withBorder: false,
                style: { paddingTop: 0 },
                title: 'Selected locations',
                children: (
                  <List
                    count={data.selectedLocations?.length}
                    header={<ListHeader titles={locationTitles} />}
                  >
                    {data.selectedLocations?.map((selectedLocationId) => {
                      const location = data.locations.find(
                        (item) => item.id === selectedLocationId
                      );

                      if (!location) {
                        return null;
                      }

                      return (
                        <LocationListCard
                          key={location.id}
                          location={location}
                          titles={locationTitles}
                          isHideForSalesAndManager={isHideForSalesAndManager}
                        />
                      );
                    })}
                  </List>
                ),
              },
            ]
          : []),
        ...(data?.payments?.length && !isHideForSalesAndManager
          ? [
              {
                withBorder: false,
                title: 'Payments',
                style: { paddingTop: 0 },
                children: <EventPayments payments={data?.payments} />,
              },
            ]
          : []),
        // CARD MACHINES (owner 2 Nov 2026): on the event page, not the
        // calendar drawer.
        ...(!isModal && data.id
          ? [
              {
                withBorder: false,
                title: 'Card machines',
                style: { paddingTop: 0 },
                children: (
                  <EventCardMachines
                    eventId={Number(data.id)}
                    hideMoney={isHideForSalesAndManager}
                  />
                ),
              },
            ]
          : []),
        ...(!isHideForSalesAndManager && (data.notes || data?.notesForClient)
          ? [
              {
                title: 'Notes',
                withBorder: false,
                style: { paddingTop: 0 },
                // One note per full-width row: both start on the page's
                // first column and a long note gets the whole width.
                rows: [
                  [
                    {
                      title: 'Notes',
                      noEllipsis: true,
                      value: freeText(data.notes),
                    },
                  ],
                  [
                    {
                      noEllipsis: true,
                      title: 'Notes for client',
                      value: freeText(data.notesForClient),
                    },
                  ],
                ],
              },
            ]
          : []),
        {
          title: 'Team',
          withBorder: false,
          style: { paddingTop: 0 },
          rows: [
            [
              {
                col: 4,
                colTablet: 6,
                value: data.leader?.name,
                title: 'Manager Assignment',
              },
              {
                col: 4,
                colTablet: 6,
                title: 'Team Assignment',
                value: data.teamMembers?.length
                  ? data.teamMembers.map((user) => user.name).join(', ')
                  : '-',
              },
            ],
          ],
        },
      ]}
    />
  );

  // TRIPS (owner 25 Oct 2026, EL-29): Logistics + Travel cost after Team,
  // as their own cards; never in the calendar drawer.
  return isModal || !data.id ? (
    details
  ) : (
    <>
      {details}
      <Suspense fallback={null}>
        <LogisticsCards eventId={Number(data.id)} />
      </Suspense>
    </>
  );
};

export { LocationListCard };
